import { useState, useCallback, useEffect } from 'react';
import { NavLink, generatePath } from 'react-router-dom';
import {
  IconAttention,
  Checkbox,
  IconSelected,
  ButtonPrimary,
  Textarea,
  IconUp,
  IconDown,
} from '@ui/src';
import {
  NewApplicationStatus,
  NewApplicationListItem,
  ReviewStatus,
  ApplicationType,
  NewApplicationCorporateInfo,
} from '../../hoc/new-application/types';
import {
  useUpdateNewApplicationMutation,
  useUpdateReviewStatusMutation,
  useUpdateCorporateReviewStatusMutation,
  useUpdateCorporateApplicationMutation,
} from '../../hoc/new-application/store/services';
import getStatusClassName from '../../hoc/new-application/helpers/get-status-class-name';
import { MatchesTypes } from './types';

import ROUTES from '../../routes';

import cn from './new-application.module.css';

const NewApplicationMatches = ({
  id,
  matches,
  setMatchSelected,
  reviewDuplicatesComment,
  applicationType,
  collapsed,
  setCollapsed,
  disabled,
  reviewedDuplicates,
  reviewStatus,
}: MatchesTypes) => {
  const [comment, setComment] = useState('');
  const [selectedApplicationsArray, setSelectedApplicationsArray] = useState<
    string[]
  >([]);

  const [skipMatches, { isLoading, isSuccess }] =
    useUpdateNewApplicationMutation();
  const [
    skipCorporateMatches,
    { isLoading: corporateIsProgress, isSuccess: corporateIsSuccess },
  ] = useUpdateCorporateApplicationMutation();
  const [updateReviewStatus, { isLoading: reviewStatusUpdate }] =
    useUpdateReviewStatusMutation();
  const [
    updateCorporateReviewStatus,
    { isLoading: corporateReviewStatusUpdate },
  ] = useUpdateCorporateReviewStatusMutation();

  const skipMatchesAction = useCallback(async () => {
    const nextReviewedDuplicates = matches
      ?.filter((item) => item.node.selected)
      .map((item) => item.node.id);
    const selectedDuplicates = matches?.filter((item) => item.node.selected);

    const body = {
      reviewedDuplicates: nextReviewedDuplicates,
      reviewDuplicatesComment: comment,
    };

    if (
      applicationType === ApplicationType.INDIVIDUAL ||
      applicationType === ApplicationType.COMPANY_OFFICIAL
    ) {
      const response = (await skipMatches({
        body,
        id,
      })) as { data: { data: NewApplicationListItem } };

      if (response.data && selectedDuplicates?.length === matches?.length) {
        updateReviewStatus({
          body: {
            reviewStatus: ReviewStatus.ACCEPTED,
          },
          id,
        });
      }
    } else {
      const response = (await skipCorporateMatches({
        body,
        id,
      })) as { data: { data: NewApplicationCorporateInfo } };

      if (response.data && selectedDuplicates?.length === matches?.length) {
        updateCorporateReviewStatus({
          body: {
            reviewStatus: ReviewStatus.ACCEPTED,
          },
          id,
        });
      }
    }
  }, [comment, matches]);

  const changeReviewStatus = () => {
    if (
      applicationType === ApplicationType.INDIVIDUAL ||
      applicationType === ApplicationType.COMPANY_OFFICIAL
    ) {
      updateReviewStatus({
        body: {
          reviewStatus: ReviewStatus.ACCEPTED,
        },
        id,
      });
    } else {
      updateCorporateReviewStatus({
        body: {
          reviewStatus: ReviewStatus.ACCEPTED,
        },
        id,
      });
    }
  };

  useEffect(() => {
    if (reviewDuplicatesComment) {
      setComment(reviewDuplicatesComment);
    }
  }, [reviewDuplicatesComment]);

  useEffect(() => {
    if (isSuccess || corporateIsSuccess) {
      setSelectedApplicationsArray([]);
    }
  }, [isSuccess, corporateIsSuccess]);

  let buttonText = 'It`s okay, skip';

  if (
    !matches?.filter((item) => !item.node.selected).length &&
    reviewStatus === ReviewStatus.HAS_DUPLICATE &&
    !selectedApplicationsArray.length
  ) {
    buttonText = 'Accept all duplicates';
  }

  return (
    <div className={cn.newApplicationMatches} id="duplicates">
      <button
        type="button"
        className={cn.newApplicationMatchesTitle}
        onClick={() => setCollapsed(!collapsed)}
      >
        <IconAttention />
        <span>
          {disabled
            ? 'The detected matches have been verified'
            : 'Found a match with existing customers'}
        </span>
        {collapsed ? (
          <IconDown className={cn.collapsedIcon} />
        ) : (
          <IconUp className={cn.collapsedIcon} />
        )}
      </button>
      {!collapsed && (
        <>
          <div className={cn.newAppliccationMatchesDescription}>
            Check to see if there really is a match. Mark the profile if the
            match is incorrect
          </div>

          {matches?.map((item) => {
            const { statusClassName } = getStatusClassName({
              status: item.node.status as NewApplicationStatus,
            });

            let name = `${item.node.firstName || '-'} ${
              item.node.lastName || '-'
            }`;
            let url = ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO;

            if (item.node.type === ApplicationType.CORPORATE) {
              name = item.node.companyName || '-';
              url = ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO;
            }

            return (
              <div key={item.node.id} className={cn.matchItem}>
                <div className={cn.matchItemLeft}>
                  <div className={cn.matchItemCheckboxWrapper}>
                    <Checkbox
                      type="checkbox"
                      name={`checkbox-${item.node.id}`}
                      title={name}
                      checked={
                        item.node.selected ||
                        disabled ||
                        Boolean(
                          reviewedDuplicates?.find(
                            (duplicate) => item.node.id === duplicate,
                          ),
                        )
                      }
                      onChange={(e) => {
                        setMatchSelected({
                          id: item.node.id,
                          selected: e.target.checked,
                        });

                        let nextSelectedApplicationsArray = (
                          [] as string[]
                        ).concat(selectedApplicationsArray);

                        if (e.target.checked) {
                          nextSelectedApplicationsArray.push(item.node.id);
                        } else {
                          nextSelectedApplicationsArray =
                            nextSelectedApplicationsArray.filter(
                              (selected) => selected !== item.node.id,
                            );
                        }

                        setSelectedApplicationsArray(
                          nextSelectedApplicationsArray,
                        );
                      }}
                      disabled={
                        disabled ||
                        Boolean(
                          reviewedDuplicates?.find(
                            (duplicate) => item.node.id === duplicate,
                          ),
                        )
                      }
                    />
                  </div>
                  <div className={cn.matchItemHintWrapper}>
                    <div className={cn.matchItemHint}>
                      Application:
                      <span
                        className={`${cn.applicationStatus} ${statusClassName}`}
                      >
                        {`${item?.node.status?.charAt(0)}${item?.node.status
                          ?.slice(1)
                          .toLowerCase()}`}
                      </span>
                    </div>
                  </div>
                </div>
                <NavLink
                  to={generatePath(url, {
                    applicationId: item.node.id,
                  })}
                  className={cn.matchItemLink}
                  target="_blanl"
                >
                  See application
                </NavLink>
              </div>
            );
          })}
          <div className={cn.matchesCommentWrapper}>
            <Textarea
              name="match-comment"
              placeholder="Enter comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className={cn.commentTextarea}
              disabled={disabled}
            />
          </div>
          {!disabled && (
            <ButtonPrimary
              text={
                isLoading ||
                reviewStatusUpdate ||
                corporateIsProgress ||
                corporateReviewStatusUpdate
                  ? 'Loading...'
                  : buttonText
              }
              className={cn.skipButton}
              icon={
                isLoading ||
                reviewStatusUpdate ||
                corporateIsProgress ||
                corporateReviewStatusUpdate ? undefined : (
                  <IconSelected width={18} height={16} />
                )
              }
              onClick={() =>
                !matches?.filter((item) => !item.node.selected).length &&
                reviewStatus === ReviewStatus.HAS_DUPLICATE &&
                !selectedApplicationsArray.length
                  ? changeReviewStatus()
                  : skipMatchesAction()
              }
              disabled={
                (matches?.filter((item) => !item.node.selected)
                  ?.length as number) > 0
                  ? Boolean(
                      !selectedApplicationsArray.length || !comment.length,
                    )
                  : !matches?.filter((item) => !item.node.selected).length &&
                    !selectedApplicationsArray.length &&
                    reviewStatus === ReviewStatus.HAS_DUPLICATE
              }
              bold
            />
          )}
        </>
      )}
    </div>
  );
};

export default NewApplicationMatches;
